import { useEffect, useRef } from "react";
import { Link, NavLink, useParams } from "react-router-dom";
import { POLICIES, COMPANY, UPDATED } from "../data/policies";
import { Seo, breadcrumbLd } from "../components/Seo";
import NotFound from "./NotFound";

const SideNav = ({ active }) => {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current?.querySelector('[aria-current="page"]');
    const nav = ref.current;
    if (el && nav && nav.scrollWidth > nav.clientWidth) nav.scrollTo({ left: el.offsetLeft - nav.clientWidth / 2 + el.clientWidth / 2, behavior: "smooth" });
  }, [active]);
  return (
  <nav ref={ref} aria-label="Policies" className="flex gap-2 overflow-x-auto pb-2 no-scrollbar lg:flex-col lg:gap-1 lg:overflow-visible">
    {POLICIES.map((p) => (
      <NavLink key={p.slug} to={`/policies/${p.slug}`} data-testid={`policy-nav-${p.slug}`}
        className={`shrink-0 rounded-full px-4 py-2 text-sm font-medium transition-colors lg:rounded-xl ${active === p.slug ? "bg-walnut text-linen" : "hover:bg-card"}`}>
        {p.title}
      </NavLink>
    ))}
  </nav>
  );
};

export function PoliciesIndex() {
  return (
    <div data-testid="policies-page" className="container-hw py-10 sm:py-16">
      <Seo title="Policies – Terms, Privacy, Shipping & Returns | H&W Furniture" description={`Terms, privacy, shipping, returns, cancellation, payment and warranty policies of H&W Furniture, operated by ${COMPANY}.`} path="/policies" />
      <p className="eyebrow">Policies</p>
      <h1 className="t-h1 mt-3">Plain-language policies.</h1>
      <p className="t-lead mt-4 max-w-xl">{`H&W Furniture is operated by ${COMPANY}, Hyderabad. Here's exactly how we handle orders, delivery, payments, returns and your data.`}</p>
      <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {POLICIES.map((p) => (
          <Link key={p.slug} to={`/policies/${p.slug}`} data-testid={`policy-card-${p.slug}`} className="card card-hover group p-6">
            <h2 className="t-h3 group-hover:text-sheesham">{p.title}</h2>
            <p className="t-small mt-2 line-clamp-2">{p.intro}</p>
          </Link>
        ))}
      </div>
    </div>
  );
}

export function PolicyPage() {
  const { slug } = useParams();
  const p = POLICIES.find((x) => x.slug === slug);
  if (!p) return <NotFound />;
  return (
    <div data-testid={`policy-page-${p.slug}`} className="container-hw grid grid-cols-1 gap-10 py-10 sm:py-16 lg:grid-cols-12">
      <Seo title={`${p.title} | H&W Furniture`} description={p.intro} path={`/policies/${p.slug}`} jsonLd={[breadcrumbLd([["Home", "/"], ["Policies", "/policies"], [p.title, `/policies/${p.slug}`]])]} />
      <aside className="min-w-0 lg:col-span-3"><div className="lg:sticky lg:top-28"><SideNav active={p.slug} /></div></aside>
      <article className="max-w-2xl lg:col-span-8 lg:col-start-5">
        <p className="eyebrow">Policies · Updated {UPDATED}</p>
        <h1 className="t-h1 mt-3">{p.title}</h1>
        <p className="t-lead mt-5">{p.intro}</p>
        <ol className="mt-10 space-y-8">
          {p.sections.map(([h, t], i) => (
            <li key={h} className="border-t border-jute/50 pt-6">
              <h2 className="t-h4 flex gap-3"><span className="price text-neem">{String(i + 1).padStart(2, "0")}</span>{h}</h2>
              <p className="t-body mt-3 text-foreground/85">{t}</p>
            </li>
          ))}
        </ol>
      </article>
    </div>
  );
}
